import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../../../axiosInstance';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DataTableComponent from '../../common/DataTable/PrimeDataTable';
import PublishActivateDeactivateModal from '../../common/PublishActivateDeactivateModal';
import { convertUTCToLocal } from '../../utils/dateUtils';

const WFList = ({ persona }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [confirmActivateModal, setConfirmActivateModal] = useState({
    open: false,
    record: null,
  });

  const navigate = useNavigate();
  const [paginationParams, setPaginationParams] = useState({
    limit: 10,
    offset: 0,
    sortField: 'createdAt',
    sortOrder: -1,
    filters: {},
  });

  const onDataTableEvent = useCallback((event) => {
    setPaginationParams((prev) => ({
      ...prev,
      limit: event.rows,
      offset: event.first,
      sortField: event.sortField || prev.sortField,
      sortOrder: event.sortOrder || prev.sortOrder,
    }));
  }, []);

  const fetchWorkflows = useCallback(async () => {
    try {
      const workflowsResponse = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/workflows`,
        { withCredentials: true }
      );
      const serverWorkflowData = (workflowsResponse?.data || []).map(
        (item) => ({
          ...item,
          type: 'workflow',
        })
      );

      const draftsResponse = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/api/workflows-drafts`,
        { withCredentials: true }
      );
      const serverDraftsData = (draftsResponse?.data || []).map((item) => ({
        ...item,
        type: 'draft',
      }));

      setData([...serverWorkflowData, ...serverDraftsData]);
    } catch (err) {
      console.error('Error fetching workflows:', err);
    }
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const columns = useMemo(
    () => [
      { field: 'id', header: t('workflowList.id'), sortable: true },
      {
        field: 'name',
        header: t('name'),
        sortable: true,
        filter: true,
        body: (rowData) =>
          rowData.type === 'draft' ? `DRFT:${rowData.name}` : rowData.name,
      },
      {
        field: 'num_stages',
        header: t('num_stages'),
        sortable: true,
        filter: true,
        body: (rowData) =>
          rowData.WorkflowVersions && rowData.WorkflowVersions[0]
            ? rowData.WorkflowVersions[0].num_stages
            : rowData.type === 'draft'
              ? rowData.num_stages
              : 'N/A',
      },
      {
        field: 'createdBy',
        header: t('createdBy'),
        sortable: true,
        filter: true,
      },
      {
        field: 'createdAt',
        header: t('createdAt'),
        sortable: true,
        filter: true,
        body: (rowData) => convertUTCToLocal(rowData.createdAt),
      },
    ],
    [t]
  );

  const actions = useMemo(
    () => [
      {
        text: t('View Versions'),
        color: 'secondary',
        onClick: (row) =>
          navigate(
            row.type === 'draft'
              ? `/${persona}/workflows/draft/${row.id}`
              : `/${persona}/workflows/${row.id}/versions`
          ),
      },
      {
        text: t('workflowList.publishActivateDeactivate'),
        color: 'primary',
        visible: (row) => row.publishedAt && row.publishedAt.trim() !== '',
        onClick: (row) => setConfirmActivateModal({ open: true, record: row }),
        status: 'success',
      },
    ],
    [t, navigate, persona]
  );

  return (
    <div>
      <DataTableComponent
        data={data}
        columns={columns}
        actions={actions}
        onDataTableEvent={onDataTableEvent}
        paginationParams={paginationParams}
        totalRecords={data?.length}
        enableCreate={true}
        enablePagination={true}
        enableLazyLoading={false}
        enableExport={true}
      />

      {confirmActivateModal.open && (
        <PublishActivateDeactivateModal
          sentData={confirmActivateModal.record}
          onEvent={setConfirmActivateModal}
          onConfirm={fetchWorkflows}
          apiPath="workflows"
          axios={axiosInstance}
          columnName="name"
          dbAction="status"
          sectionName={t('workflowList.workflows')}
        />
      )}
    </div>
  );
};

export default WFList;
